import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  KeyRound, Webhook, Plus, Copy, Trash2, Power, PowerOff, Eye,
  AlertTriangle, CheckCircle2, XCircle, Clock, ExternalLink, ShieldAlert,
  RefreshCw, Code2,
  Calendar, Video, MessageSquare, HardDrive, FileSignature, Briefcase,
  Plug, Sparkles, Info, Mail,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

const CopyButton = ({ text, label = 'Copy', testid }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      data-testid={testid}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          toast.success(`${label} copied`);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          toast.error('Copy failed — please copy manually');
        }
      }}
    >
      {copied ? <CheckCircle2 className="h-4 w-4 mr-1.5" /> : <Copy className="h-4 w-4 mr-1.5" />}
      {copied ? 'Copied' : label}
    </Button>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// API Keys panel
// ─────────────────────────────────────────────────────────────────────────────
function ApiKeysPanel() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null); // shown once

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/integrations/api-keys`, { headers: authHeader() });
      setKeys(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e?.preventDefault();
    const name = newKeyName.trim();
    if (name.length < 2) { toast.error('Name must be at least 2 characters'); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/integrations/api-keys`, { name }, { headers: authHeader() });
      setRevealedKey(res.data);
      setCreateOpen(false);
      setNewKeyName('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (key) => {
    if (!window.confirm(`Revoke API key "${key.name}"? This action cannot be undone — any integrations using it will immediately stop working.`)) return;
    try {
      await axios.post(`${API}/integrations/api-keys/${key.id}/revoke`, {}, { headers: authHeader() });
      toast.success('API key revoked');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revoke key');
    }
  };

  const active = keys.filter(k => !k.revoked);
  const revoked = keys.filter(k => k.revoked);

  return (
    <Card data-testid="api-keys-panel">
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-blue-600" />
            API Keys
          </CardTitle>
          <CardDescription>
            Long-lived server-to-server credentials for the Recruitizr Open API. Use
            them in <code className="text-xs bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">Authorization: Bearer rk_live_...</code>
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="api-keys-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1.5" />
            Refresh
          </Button>
          <Button onClick={() => setCreateOpen(true)} data-testid="api-keys-new-btn">
            <Plus className="h-4 w-4 mr-1.5" />
            New API Key
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-10 text-center text-sm text-slate-500" data-testid="api-keys-loading">Loading...</div>
        ) : keys.length === 0 ? (
          <div className="py-10 text-center" data-testid="api-keys-empty">
            <KeyRound className="h-10 w-10 mx-auto text-slate-300 mb-2" />
            <div className="text-sm text-slate-500">No API keys yet.</div>
            <div className="text-xs text-slate-400 mt-1">Create one to start integrating Recruitizr with your tools.</div>
          </div>
        ) : (
          <Table data-testid="api-keys-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Key prefix</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Last used</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...active, ...revoked].map(k => (
                <TableRow key={k.id} data-testid={`api-key-row-${k.id}`}>
                  <TableCell className="font-medium">{k.name}</TableCell>
                  <TableCell><code className="text-xs">{k.key_prefix}…</code></TableCell>
                  <TableCell className="text-sm text-slate-500">{fmtDate(k.created_at)}</TableCell>
                  <TableCell className="text-sm text-slate-500">{fmtDate(k.last_used_at)}</TableCell>
                  <TableCell>
                    {k.revoked
                      ? <Badge variant="secondary" className="bg-slate-200 text-slate-700">Revoked</Badge>
                      : <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Active</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    {!k.revoked && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleRevoke(k)}
                        data-testid={`api-key-revoke-btn-${k.id}`}
                      >
                        <Trash2 className="h-4 w-4 mr-1.5" />
                        Revoke
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="api-key-create-dialog">
          <DialogHeader>
            <DialogTitle>Create new API key</DialogTitle>
            <DialogDescription>
              Give this key a memorable name so you can identify it later (e.g. {'"Zapier prod"'}, {'"Internal HRMS sync"'}).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3">
            <div>
              <Label htmlFor="api-key-name">Name</Label>
              <Input
                id="api-key-name"
                data-testid="api-key-name-input"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="e.g. Zapier production"
                maxLength={80}
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
              <Button type="submit" disabled={creating} data-testid="api-key-create-submit-btn">
                {creating ? 'Creating…' : 'Create key'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reveal-once dialog */}
      <Dialog open={!!revealedKey} onOpenChange={(o) => !o && setRevealedKey(null)}>
        <DialogContent data-testid="api-key-reveal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-600" />
              Save your API key
            </DialogTitle>
            <DialogDescription>
              This is the <strong>only time</strong> we&apos;ll show this key in full. Store it in a secure secret manager — you won&apos;t be able to retrieve it later.
            </DialogDescription>
          </DialogHeader>
          {revealedKey && (
            <div className="space-y-3">
              <Alert className="border-amber-300 bg-amber-50 text-amber-900">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  Anyone with this key can call the Recruitizr API on behalf of your tenant. Treat it like a password.
                </AlertDescription>
              </Alert>
              <div>
                <Label className="text-xs text-slate-500">Name</Label>
                <div className="text-sm font-medium">{revealedKey.name}</div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">API key</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 text-xs bg-slate-900 text-emerald-300 p-3 rounded font-mono break-all" data-testid="api-key-reveal-value">
                    {revealedKey.api_key}
                  </code>
                </div>
                <div className="mt-2">
                  <CopyButton text={revealedKey.api_key} label="Copy key" testid="api-key-reveal-copy-btn" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setRevealedKey(null)} data-testid="api-key-reveal-done-btn">I{'\''}ve saved it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Webhooks panel
// ─────────────────────────────────────────────────────────────────────────────
function WebhooksPanel() {
  const [subs, setSubs] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', url: '', events: [] });
  const [revealedSecret, setRevealedSecret] = useState(null);
  const [deliveriesFor, setDeliveriesFor] = useState(null); // subscription
  const [deliveries, setDeliveries] = useState([]);
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [subsRes, evRes] = await Promise.all([
        axios.get(`${API}/integrations/webhooks`, { headers: authHeader() }),
        axios.get(`${API}/integrations/webhook-events`, { headers: authHeader() }),
      ]);
      setSubs(Array.isArray(subsRes.data) ? subsRes.data : []);
      setEvents(evRes.data?.events || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({ name: '', url: '', events: [] });
    setCreateOpen(true);
  };

  const toggleEvent = (ev) => {
    setForm(f => ({
      ...f,
      events: f.events.includes(ev) ? f.events.filter(x => x !== ev) : [...f.events, ev],
    }));
  };

  const handleCreate = async (e) => {
    e?.preventDefault();
    if (form.name.trim().length < 2) { toast.error('Name must be at least 2 characters'); return; }
    if (!/^https?:\/\//.test(form.url)) { toast.error('URL must start with http:// or https://'); return; }
    if (form.events.length === 0) { toast.error('Select at least one event to subscribe to'); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/integrations/webhooks`, {
        name: form.name.trim(),
        url: form.url.trim(),
        events: form.events,
      }, { headers: authHeader() });
      setRevealedSecret(res.data);
      setCreateOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create webhook');
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (sub) => {
    try {
      await axios.post(`${API}/integrations/webhooks/${sub.id}/toggle`, {}, { headers: authHeader() });
      toast.success(`Webhook ${sub.active ? 'paused' : 'resumed'}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to toggle webhook');
    }
  };

  const handleDelete = async (sub) => {
    if (!window.confirm(`Delete webhook "${sub.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/integrations/webhooks/${sub.id}`, { headers: authHeader() });
      toast.success('Webhook deleted');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete webhook');
    }
  };

  const openDeliveries = async (sub) => {
    setDeliveriesFor(sub);
    setDeliveriesLoading(true);
    setDeliveries([]);
    try {
      const res = await axios.get(`${API}/integrations/webhooks/${sub.id}/deliveries`, { headers: authHeader() });
      setDeliveries(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load deliveries');
    } finally {
      setDeliveriesLoading(false);
    }
  };

  return (
    <Card data-testid="webhooks-panel">
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Webhook className="h-5 w-5 text-blue-600" />
            Webhooks
          </CardTitle>
          <CardDescription>
            Get real-time HTTP callbacks when things happen in your Recruitizr tenant.
            Each delivery is signed with HMAC-SHA256 in <code className="text-xs bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">X-Recruitizr-Signature</code>.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="webhooks-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1.5" />
            Refresh
          </Button>
          <Button onClick={openCreate} data-testid="webhooks-new-btn">
            <Plus className="h-4 w-4 mr-1.5" />
            New Webhook
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-10 text-center text-sm text-slate-500" data-testid="webhooks-loading">Loading...</div>
        ) : subs.length === 0 ? (
          <div className="py-10 text-center" data-testid="webhooks-empty">
            <Webhook className="h-10 w-10 mx-auto text-slate-300 mb-2" />
            <div className="text-sm text-slate-500">No webhooks configured.</div>
            <div className="text-xs text-slate-400 mt-1">Subscribe to events like candidate.created, interview.scheduled, and more.</div>
          </div>
        ) : (
          <Table data-testid="webhooks-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>URL</TableHead>
                <TableHead>Events</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subs.map(sub => (
                <TableRow key={sub.id} data-testid={`webhook-row-${sub.id}`}>
                  <TableCell className="font-medium">{sub.name}</TableCell>
                  <TableCell className="max-w-[260px] truncate">
                    <a href={sub.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1 text-sm">
                      {sub.url}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1 max-w-[220px]">
                      {(sub.events || []).slice(0, 3).map(ev => (
                        <Badge key={ev} variant="secondary" className="text-[10px]">{ev}</Badge>
                      ))}
                      {(sub.events || []).length > 3 && (
                        <Badge variant="secondary" className="text-[10px]">+{sub.events.length - 3}</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {sub.active
                      ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Active</Badge>
                      : <Badge variant="secondary">Paused</Badge>}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => openDeliveries(sub)} data-testid={`webhook-deliveries-btn-${sub.id}`}>
                      <Eye className="h-4 w-4 mr-1.5" />
                      Deliveries
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleToggle(sub)} data-testid={`webhook-toggle-btn-${sub.id}`}>
                      {sub.active ? <PowerOff className="h-4 w-4 mr-1.5" /> : <Power className="h-4 w-4 mr-1.5" />}
                      {sub.active ? 'Pause' : 'Resume'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(sub)}
                      data-testid={`webhook-delete-btn-${sub.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="webhook-create-dialog">
          <DialogHeader>
            <DialogTitle>Create webhook subscription</DialogTitle>
            <DialogDescription>
              We{'\''}ll POST signed JSON payloads to your URL whenever any of the
              selected events occur in your tenant.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3">
            <div>
              <Label htmlFor="webhook-name">Name</Label>
              <Input
                id="webhook-name"
                data-testid="webhook-name-input"
                value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Slack notifier"
                maxLength={80}
              />
            </div>
            <div>
              <Label htmlFor="webhook-url">Endpoint URL</Label>
              <Input
                id="webhook-url"
                data-testid="webhook-url-input"
                value={form.url}
                onChange={(e) => setForm(f => ({ ...f, url: e.target.value }))}
                placeholder="https://hooks.example.com/recruitizr"
                type="url"
              />
            </div>
            <div>
              <Label className="mb-2 block">Events to subscribe to</Label>
              {events.length === 0 ? (
                <div className="text-xs text-slate-400">Loading event list...</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto p-2 border rounded">
                  {events.map(ev => (
                    <label key={ev} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 rounded px-1.5 py-1">
                      <Checkbox
                        checked={form.events.includes(ev)}
                        onCheckedChange={() => toggleEvent(ev)}
                        data-testid={`webhook-event-checkbox-${ev}`}
                      />
                      <code className="text-xs">{ev}</code>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
              <Button type="submit" disabled={creating} data-testid="webhook-create-submit-btn">
                {creating ? 'Creating…' : 'Create webhook'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reveal secret dialog */}
      <Dialog open={!!revealedSecret} onOpenChange={(o) => !o && setRevealedSecret(null)}>
        <DialogContent data-testid="webhook-reveal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-600" />
              Save your signing secret
            </DialogTitle>
            <DialogDescription>
              Use this secret to verify the HMAC signature in the
              <code className="text-xs bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded mx-1">X-Recruitizr-Signature</code>
              header. We{'\''}ll never show it again.
            </DialogDescription>
          </DialogHeader>
          {revealedSecret && (
            <div className="space-y-3">
              <Alert className="border-amber-300 bg-amber-50 text-amber-900">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  Store this secret alongside your other application secrets. Anyone with
                  the secret can forge requests that look authentic to your server.
                </AlertDescription>
              </Alert>
              <div>
                <Label className="text-xs text-slate-500">Webhook</Label>
                <div className="text-sm font-medium">{revealedSecret.name} → {revealedSecret.url}</div>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Signing secret</Label>
                <code className="block text-xs bg-slate-900 text-emerald-300 p-3 rounded font-mono break-all mt-1" data-testid="webhook-reveal-secret-value">
                  {revealedSecret.secret}
                </code>
                <div className="mt-2">
                  <CopyButton text={revealedSecret.secret} label="Copy secret" testid="webhook-reveal-copy-btn" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setRevealedSecret(null)} data-testid="webhook-reveal-done-btn">I&apos;ve saved it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deliveries dialog */}
      <Dialog open={!!deliveriesFor} onOpenChange={(o) => !o && setDeliveriesFor(null)}>
        <DialogContent className="max-w-3xl" data-testid="webhook-deliveries-dialog">
          <DialogHeader>
            <DialogTitle>Recent deliveries — {deliveriesFor?.name}</DialogTitle>
            <DialogDescription>
              Last 50 attempts. Retries are spaced 2s / 10s / 60s.
            </DialogDescription>
          </DialogHeader>
          {deliveriesLoading ? (
            <div className="py-10 text-center text-sm text-slate-500">Loading...</div>
          ) : deliveries.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500" data-testid="webhook-deliveries-empty">
              No deliveries yet. They{'\''}ll appear here once an event fires.
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto">
              <Table data-testid="webhook-deliveries-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Attempts</TableHead>
                    <TableHead>Result</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deliveries.map(d => {
                    const ok = d.status_code && d.status_code >= 200 && d.status_code < 300;
                    return (
                      <TableRow key={d.id}>
                        <TableCell className="text-xs text-slate-500 whitespace-nowrap">{fmtDate(d.created_at)}</TableCell>
                        <TableCell><code className="text-xs">{d.event}</code></TableCell>
                        <TableCell>
                          {ok ? (
                            <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              {d.status_code}
                            </Badge>
                          ) : d.status_code ? (
                            <Badge variant="destructive">
                              <XCircle className="h-3 w-3 mr-1" />
                              {d.status_code}
                            </Badge>
                          ) : (
                            <Badge variant="secondary">
                              <Clock className="h-3 w-3 mr-1" />
                              Pending
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{d.attempts || 0}</TableCell>
                        <TableCell className="text-xs text-slate-500 max-w-[260px] truncate">
                          {d.error || (ok ? 'Delivered' : '—')}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => openDeliveries(deliveriesFor)} data-testid="webhook-deliveries-refresh-btn">
              <RefreshCw className="h-4 w-4 mr-1.5" />
              Refresh
            </Button>
            <Button onClick={() => setDeliveriesFor(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Quick-start snippet
// ─────────────────────────────────────────────────────────────────────────────
function QuickStartCard() {
  const apiBase = `${BACKEND_URL}/api/v1`;
  const snippet = `curl -H "Authorization: Bearer rk_live_..." \\
     ${apiBase}/me`;
  return (
    <Card data-testid="quickstart-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Code2 className="h-5 w-5 text-blue-600" />
          Quick start
        </CardTitle>
        <CardDescription>
          Verify your API key works by hitting the <code className="text-xs bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">/api/v1/me</code> endpoint.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <pre className="text-xs bg-slate-900 text-emerald-300 p-3 rounded font-mono whitespace-pre-wrap break-all">{snippet}</pre>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <CopyButton text={snippet} label="Copy snippet" testid="quickstart-copy-btn" />
          <a
            href="/developers"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline"
            data-testid="quickstart-developer-docs-link"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open developer docs
          </a>
        </div>
      </CardContent>
    </Card>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Connected Apps panel — sketch mode (design + interest / mock-connect)
// ─────────────────────────────────────────────────────────────────────────────
const CATEGORY_ICONS = {
  email: Mail,
  calendar: Calendar,
  video: Video,
  messaging: MessageSquare,
  storage: HardDrive,
  signature: FileSignature,
  job_board: Briefcase,
  custom: Plug,
};

function ConnectedAppsPanel() {
  const [catalog, setCatalog] = useState(null);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const [schema, setSchema] = useState(null);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [credentials, setCredentials] = useState({});
  const [accountLabel, setAccountLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false); // when Connected → click Update to edit

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [catRes, connRes] = await Promise.all([
        axios.get(`${API}/tenant-integrations/catalog`, { headers: authHeader() }),
        axios.get(`${API}/tenant-integrations/connections`, { headers: authHeader() }),
      ]);
      setCatalog(catRes.data);
      setConnections(Array.isArray(connRes.data) ? connRes.data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load connected apps');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const isConnected = (providerId) => connections.find(c => c.provider_id === providerId);

  const openProvider = async (provider) => {
    setSelectedProviderId(provider.id);
    setSchema(null);
    setCredentials({});
    setAccountLabel('');
    setEditMode(false);
    setSchemaLoading(true);
    try {
      const res = await axios.get(`${API}/tenant-integrations/${provider.id}/schema`, { headers: authHeader() });
      setSchema(res.data);
      const conn = isConnected(provider.id);
      if (conn) setAccountLabel(conn.account_label || '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load provider details');
      setSelectedProviderId(null);
    } finally {
      setSchemaLoading(false);
    }
  };

  const closeDialog = () => {
    setSelectedProviderId(null);
    setSchema(null);
    setCredentials({});
    setAccountLabel('');
    setEditMode(false);
  };

  const submitConnect = async () => {
    if (!schema) return;
    const missing = (schema.credential_fields || [])
      .filter(f => f.required && !(credentials[f.key] || '').toString().trim())
      .map(f => f.label);
    if (missing.length) { toast.error(`Missing: ${missing.join(', ')}`); return; }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/tenant-integrations/${schema.id}/connect`,
        { credentials, account_label: accountLabel },
        { headers: authHeader() }
      );
      toast.success(`${schema.name} connected — credentials saved (encrypted).`);
      await load();
      closeDialog();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save credentials');
    } finally {
      setSubmitting(false);
    }
  };

  const submitInterest = async () => {
    if (!schema) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/tenant-integrations/${schema.id}/interest`, { notes: '' }, { headers: authHeader() });
      toast.success("Thanks — we'll email you when this provider is available.");
      closeDialog();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not register interest');
    } finally {
      setSubmitting(false);
    }
  };

  const disconnect = async (providerId, name) => {
    if (!window.confirm(`Disconnect ${name}? The stored credentials will be permanently deleted and any workflow hooks using this app will stop firing.`)) return;
    try {
      await axios.delete(`${API}/tenant-integrations/${providerId}/disconnect`, { headers: authHeader() });
      toast.success(`${name} disconnected`);
      await load();
      closeDialog();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to disconnect');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copied'));
  };

  const conn = selectedProviderId ? isConnected(selectedProviderId) : null;
  const showForm = !conn || editMode;

  if (loading) {
    return <div className="py-10 text-center text-sm text-slate-500" data-testid="connected-apps-loading">Loading connected apps…</div>;
  }
  if (!catalog) return null;

  return (
    <div className="space-y-6" data-testid="connected-apps-panel">
      <Alert className="border-blue-300 bg-blue-50 text-blue-900">
        <Sparkles className="h-4 w-4" />
        <AlertDescription className="text-xs">
          <strong>Bring your own credentials.</strong> Create the OAuth app or API key in the
          provider&apos;s console, then paste the credentials here. They&apos;re encrypted at rest and only
          used by Recruitizr on your behalf.
        </AlertDescription>
      </Alert>

      {catalog.categories.map((cat) => {
        const providers = catalog.providers.filter(p => p.category === cat.id);
        if (providers.length === 0) return null;
        const CatIcon = CATEGORY_ICONS[cat.id] || Plug;
        return (
          <div key={cat.id} data-testid={`category-${cat.id}`}>
            <div className="flex items-center gap-2 mb-3">
              <CatIcon className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{cat.label}</h2>
              <span className="text-xs text-slate-500">— {cat.description}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {providers.map((p) => {
                const c = isConnected(p.id);
                let badge;
                if (c) {
                  badge = (
                    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px] mt-0.5">
                      <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" />Connected
                    </Badge>
                  );
                } else if (p.status === 'feed') {
                  badge = <Badge className="bg-teal-100 text-teal-800 hover:bg-teal-100 text-[10px] mt-0.5">Always on</Badge>;
                } else if (p.status === 'beta') {
                  badge = <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100 text-[10px] mt-0.5">Beta</Badge>;
                } else if (p.status === 'coming_soon') {
                  badge = <Badge variant="secondary" className="text-[10px] mt-0.5">Coming soon</Badge>;
                } else {
                  badge = <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 text-[10px] mt-0.5">Available</Badge>;
                }
                return (
                  <Card
                    key={p.id}
                    className="hover:border-blue-400 transition-colors cursor-pointer"
                    onClick={() => openProvider(p)}
                    data-testid={`provider-tile-${p.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2.5">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
                          style={{ backgroundColor: p.brand_color }}
                        >
                          {p.name.slice(0, 2).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-sm text-slate-900 dark:text-white">{p.name}</div>
                          {badge}
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 mt-2.5 line-clamp-2">{p.description}</p>
                      {c?.account_label && (
                        <div className="text-[11px] text-slate-400 mt-1.5 truncate">
                          as <code>{c.account_label}</code>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Provider detail dialog — wizard + credential form */}
      <Dialog open={!!selectedProviderId} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="provider-detail-dialog">
          {schemaLoading || !schema ? (
            <div className="py-10 text-center text-sm text-slate-500">Loading…</div>
          ) : (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center text-white text-base font-semibold flex-shrink-0"
                    style={{ backgroundColor: schema.brand_color }}
                  >
                    {schema.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <DialogTitle>{schema.name}</DialogTitle>
                    <DialogDescription className="text-xs">{schema.description}</DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-4">
                {/* What this will do */}
                <div>
                  <Label className="text-xs text-slate-500 flex items-center gap-1.5">
                    <Info className="h-3 w-3" /> Where this will be used
                  </Label>
                  <ul className="mt-1.5 space-y-1 text-sm">
                    {(schema.workflow_hooks || []).map((h, i) => (
                      <li key={i} className="flex items-start gap-2 text-slate-700 dark:text-slate-300">
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span className="text-xs">{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Connected panel */}
                {conn && !editMode && (
                  <div className="border rounded-md p-3 bg-emerald-50 border-emerald-200 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-sm font-medium text-emerald-900">
                          Connected as {conn.account_label}
                        </div>
                        <div className="text-xs text-emerald-700 mt-0.5">
                          Saved {fmtDate(conn.connected_at)} · Credentials encrypted at rest.
                        </div>
                      </div>
                      <ShieldAlert className="h-4 w-4 text-emerald-600" />
                    </div>
                    <div className="border-t border-emerald-200 pt-2 space-y-1">
                      {(schema.credential_fields || []).map(f => (
                        <div key={f.key} className="flex justify-between text-xs">
                          <span className="text-emerald-800">{f.label}</span>
                          <code className="text-emerald-900 font-mono max-w-[240px] truncate">
                            {conn.credentials_preview?.[f.key] || '—'}
                          </code>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2 pt-1 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => setEditMode(true)} data-testid="provider-edit-btn">
                        Update credentials
                      </Button>
                      {schema.auth_type === 'oauth2' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className={conn.authorized ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-amber-300 text-amber-700 hover:bg-amber-50"}
                          onClick={async () => {
                            try {
                              setSubmitting(true);
                              const res = await axios.get(`${API}/tenant-integrations/${schema.id}/oauth/start`, { headers: authHeader() });
                              const popup = window.open(res.data.auth_url, 'recruitizr-oauth', 'width=520,height=650');
                              // Listen for callback → refresh connections
                              const listener = (ev) => {
                                if (ev.data?.type === 'recruitizr-oauth') {
                                  window.removeEventListener('message', listener);
                                  if (ev.data.ok) {
                                    toast.success('Authorization successful');
                                    load();
                                    setTimeout(() => openProvider(schema), 400);
                                  } else {
                                    toast.error('Authorization failed');
                                  }
                                }
                              };
                              window.addEventListener('message', listener);
                              // Fallback: poll for popup close then refresh
                              const poll = setInterval(() => {
                                if (popup && popup.closed) {
                                  clearInterval(poll);
                                  window.removeEventListener('message', listener);
                                  load();
                                }
                              }, 800);
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || 'Could not start OAuth');
                            } finally { setSubmitting(false); }
                          }}
                          disabled={submitting}
                          data-testid="provider-authorize-btn"
                        >
                          {conn.authorized ? 'Reauthorize' : 'Authorize with ' + (
                            schema.id.startsWith('google') || schema.id.startsWith('gmail') ? 'Google' :
                            schema.id.startsWith('outlook') || schema.id.startsWith('ms_') || schema.id === 'onedrive' ? 'Microsoft' :
                            schema.id.startsWith('zoho') ? 'Zoho' :
                            schema.id.startsWith('linkedin') ? 'LinkedIn' :
                            schema.name
                          )}
                        </Button>
                      )}
                      {(schema.id === 'slack' || schema.category === 'email' || schema.category === 'custom') && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-blue-300 text-blue-700 hover:bg-blue-50"
                          onClick={async () => {
                            try {
                              setSubmitting(true);
                              const res = await axios.post(`${API}/tenant-integrations/${schema.id}/test`, {}, { headers: authHeader() });
                              if (res.data?.ok === false) {
                                toast.error(res.data?.detail || 'Test failed');
                              } else {
                                toast.success(res.data?.detail || 'Test succeeded.');
                              }
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || 'Test failed');
                            } finally { setSubmitting(false); }
                          }}
                          disabled={submitting}
                          data-testid="provider-test-btn"
                        >
                          {submitting ? 'Sending…' : (
                            schema.category === 'email' ? 'Send test email' :
                            schema.id === 'custom_webhook' ? 'Send test ping' :
                            schema.id === 'custom_api' ? 'Test API call' :
                            'Send test message'
                          )}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        onClick={() => disconnect(schema.id, schema.name)}
                        data-testid="provider-disconnect-btn"
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1.5" />Disconnect
                      </Button>
                    </div>
                    {schema.auth_type === 'oauth2' && !conn.authorized && (
                      <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1">
                        Credentials saved. Complete authorization to activate workflow hooks.
                      </div>
                    )}
                    {schema.auth_type === 'oauth2' && conn.authorized && (
                      <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 mt-1">
                        Fully authorized. Workflow hooks are firing.
                      </div>
                    )}
                  </div>
                )}

                {/* Wizard + form */}
                {showForm && schema.auth_type !== 'feed' && schema.status !== 'coming_soon' && (
                  <>
                    <div>
                      <Label className="text-xs text-slate-500 flex items-center gap-1.5 mb-2">
                        <KeyRound className="h-3 w-3" /> Setup guide
                      </Label>
                      <ol className="space-y-2.5">
                        {(schema.setup_guide?.steps || []).map(step => (
                          <li key={step.n} className="flex gap-2.5">
                            <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
                              {step.n}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-slate-900 dark:text-white">{step.title}</div>
                              <div className="text-xs text-slate-500 mt-0.5">{step.description}</div>
                              {step.action?.url && (
                                <a
                                  href={step.action.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-1"
                                >
                                  {step.action.label} <ExternalLink className="h-2.5 w-2.5" />
                                </a>
                              )}
                            </div>
                          </li>
                        ))}
                      </ol>
                      {schema.setup_guide?.api_hint && (
                        <div className="mt-2 text-xs text-slate-500">
                          <strong>API to enable:</strong> {schema.setup_guide.api_hint}
                        </div>
                      )}
                    </div>

                    {/* Redirect URI helper (for OAuth2) */}
                    {schema.auth_type === 'oauth2' && schema.redirect_uri && (
                      <div className="border rounded-md p-2.5 bg-slate-50 dark:bg-slate-900">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <Label className="text-xs text-slate-500">Redirect URI to register with the provider</Label>
                            <code className="block mt-1 text-xs font-mono text-slate-800 dark:text-slate-200 break-all">
                              {schema.redirect_uri}
                            </code>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyToClipboard(schema.redirect_uri)}
                            data-testid="copy-redirect-uri"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Credential form */}
                    <div className="space-y-2.5 border-t pt-3">
                      <Label className="text-xs text-slate-500 flex items-center gap-1.5 mb-1">
                        <ShieldAlert className="h-3 w-3" /> Credentials (encrypted at rest)
                      </Label>
                      {(schema.credential_fields || []).map(f => (
                        <div key={f.key}>
                          <Label htmlFor={`field-${f.key}`} className="text-xs">
                            {f.label} {f.required && <span className="text-red-500">*</span>}
                          </Label>
                          {f.help_text && (
                            <p className="text-[11px] text-slate-400 mt-0.5 mb-1">{f.help_text}</p>
                          )}
                          {f.key === 'private_key' ? (
                            <textarea
                              id={`field-${f.key}`}
                              className="w-full mt-1 text-xs font-mono border rounded p-2 min-h-[120px] bg-white dark:bg-slate-900"
                              value={credentials[f.key] || ''}
                              onChange={e => setCredentials({ ...credentials, [f.key]: e.target.value })}
                              placeholder={f.placeholder}
                              data-testid={`field-${f.key}`}
                            />
                          ) : f.type === 'select' ? (
                            <select
                              id={`field-${f.key}`}
                              className="w-full mt-1 text-sm border rounded p-2 bg-white dark:bg-slate-900"
                              value={credentials[f.key] ?? f.default ?? ''}
                              onChange={e => setCredentials({ ...credentials, [f.key]: e.target.value })}
                              data-testid={`field-${f.key}`}
                            >
                              {(f.options || []).map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          ) : (
                            <Input
                              id={`field-${f.key}`}
                              type={f.type === 'password' ? 'password' : (f.type === 'email' ? 'email' : 'text')}
                              value={credentials[f.key] || ''}
                              onChange={e => setCredentials({ ...credentials, [f.key]: e.target.value })}
                              placeholder={f.placeholder}
                              className="mt-1"
                              data-testid={`field-${f.key}`}
                            />
                          )}
                        </div>
                      ))}
                      <div>
                        <Label htmlFor="field-account-label" className="text-xs">Account label (optional)</Label>
                        <Input
                          id="field-account-label"
                          value={accountLabel}
                          onChange={e => setAccountLabel(e.target.value)}
                          placeholder="e.g. hr@company.com — helps you recognise this connection later"
                          className="mt-1"
                          data-testid="field-account-label"
                        />
                      </div>
                    </div>
                    {schema.auth_type === 'oauth2' && (
                      <Alert className="border-emerald-200 bg-emerald-50 text-emerald-900 py-2">
                        <AlertDescription className="text-[11px]">
                          Save credentials, then hit <strong>Authorize with {schema.name}</strong> — a popup will
                          take you through the OAuth consent flow. Recruitizr never sees your password;
                          only the tokens the provider issues after you grant access.
                        </AlertDescription>
                      </Alert>
                    )}
                  </>
                )}

                {/* Feed provider — no creds */}
                {schema.auth_type === 'feed' && (
                  <div className="border rounded-md p-3 bg-teal-50 border-teal-200">
                    <div className="text-sm font-medium text-teal-900 mb-1.5">No setup needed</div>
                    <ol className="space-y-1.5">
                      {(schema.setup_guide?.steps || []).map(step => (
                        <li key={step.n} className="text-xs text-teal-800">
                          <strong>{step.n}. {step.title}:</strong> {step.description}
                          {step.action?.url && (
                            <a href={step.action.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 ml-1 text-blue-600 hover:underline">
                              {step.action.label} <ExternalLink className="h-2.5 w-2.5" />
                            </a>
                          )}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {/* Coming soon — interest capture only */}
                {showForm && schema.status === 'coming_soon' && (
                  <Alert className="border-slate-200 bg-slate-50">
                    <AlertDescription className="text-xs text-slate-700">
                      This provider isn&apos;t supported yet. Register your interest and we&apos;ll notify you when it&apos;s ready.
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              <DialogFooter>
                {schema.auth_type === 'feed' ? (
                  <Button variant="outline" onClick={closeDialog}>Close</Button>
                ) : showForm && schema.status === 'coming_soon' ? (
                  <>
                    <Button variant="outline" onClick={closeDialog} disabled={submitting}>Cancel</Button>
                    <Button onClick={submitInterest} disabled={submitting} data-testid="interest-submit-btn">
                      <Sparkles className="h-4 w-4 mr-1.5" />
                      {submitting ? 'Sending…' : 'Notify me'}
                    </Button>
                  </>
                ) : showForm ? (
                  <>
                    <Button variant="outline" onClick={editMode ? () => setEditMode(false) : closeDialog} disabled={submitting}>
                      {editMode ? 'Back' : 'Cancel'}
                    </Button>
                    <Button onClick={submitConnect} disabled={submitting} data-testid="connect-submit-btn">
                      <KeyRound className="h-4 w-4 mr-1.5" />
                      {submitting ? 'Saving…' : (conn ? 'Update credentials' : 'Save credentials')}
                    </Button>
                  </>
                ) : (
                  <Button variant="outline" onClick={closeDialog}>Close</Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────
export default function Integrations() {
  return (
    <div className="p-6 space-y-6" data-testid="integrations-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <KeyRound className="h-6 w-6 text-blue-600" />
          Open API & Integrations
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Manage API keys and webhook subscriptions for your Recruitizr tenant. Build custom
          integrations, automate workflows, and connect Recruitizr to the rest of your stack.
        </p>
      </div>

      <Tabs defaultValue="connected-apps" className="w-full">
        <TabsList data-testid="integrations-tabs">
          <TabsTrigger value="connected-apps" data-testid="tab-connected-apps">
            <Plug className="h-4 w-4 mr-1.5" />
            Connected Apps
          </TabsTrigger>
          <TabsTrigger value="api-keys" data-testid="tab-api-keys">
            <KeyRound className="h-4 w-4 mr-1.5" />
            API Keys
          </TabsTrigger>
          <TabsTrigger value="webhooks" data-testid="tab-webhooks">
            <Webhook className="h-4 w-4 mr-1.5" />
            Webhooks
          </TabsTrigger>
        </TabsList>

        <TabsContent value="connected-apps" className="mt-4">
          <ConnectedAppsPanel />
        </TabsContent>

        <TabsContent value="api-keys" className="space-y-6 mt-4">
          <ApiKeysPanel />
          <QuickStartCard />
        </TabsContent>

        <TabsContent value="webhooks" className="mt-4">
          <WebhooksPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
